import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Bug, FlaskConical, TrendingDown, AlertTriangle } from "lucide-react";

function ProgressRing({ percent }: { percent: number }) {
  const r = 28;
  const c = 2 * Math.PI * r;
  const offset = c - (percent / 100) * c;
  return (
    <svg width="68" height="68" viewBox="0 0 68 68" className="rotate-[-90deg]">
      <defs>
        <linearGradient id="progressGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(177 58% 52%)" />
          <stop offset="100%" stopColor="hsl(213 70% 51%)" />
        </linearGradient>
      </defs>
      <circle cx="34" cy="34" r={r} fill="none" stroke="hsl(210 20% 90%)" strokeWidth="5" />
      <circle cx="34" cy="34" r={r} fill="none" stroke="url(#progressGradient)" strokeWidth="5"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
        className="transition-all duration-1000" />
      <text x="34" y="34" textAnchor="middle" dominantBaseline="central"
        className="rotate-90 origin-center" fill="hsl(210 15% 12%)" fontSize="13" fontWeight="700">
        {percent}%
      </text>
    </svg>
  );
}

function FloatingCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`absolute bg-white rounded-xl shadow-elevated px-3 py-2 text-xs font-medium ${className}`}>
      {children}
    </div>
  );
}

export function HeroSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section id="hero" className="relative overflow-hidden min-h-screen flex flex-col justify-center pt-24 lg:pt-24 pb-16 bg-white landing-section-contained" style={{ paddingTop: "calc(6rem + var(--public-app-banner-offset, 0px))" }}>
      {/* Decorative circles */}
      <div className="absolute top-0 left-0 w-40 h-40 rounded-full bg-landing-accent/5 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute top-1/3 right-1/4 w-32 h-32 rounded-full bg-landing-accent/5 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left content — CSS animate on mount */}
          <div className="animate-fade-in">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.1] mb-6">
              {t("landing.hero.titleLine1", "Built by growers,")}<br />
              <span className="bg-gradient-to-r from-[hsl(177,58%,52%)] to-[hsl(213,70%,51%)] bg-clip-text text-transparent">{t("landing.hero.titleHighlight", "for growers.")}</span>
            </h1>
            <p className="text-2xl sm:text-3xl lg:text-4xl text-gray-900 max-w-lg mb-10 leading-relaxed whitespace-pre-line">
              {t("landing.hero.tagline", "Less guesswork.\nBetter planning.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:items-start w-fit sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto gap-2 text-lg px-8 py-4 bg-gradient-to-r from-[hsl(177,58%,52%)] to-[hsl(213,70%,51%)] text-white font-semibold hover:opacity-90 rounded-xl"
                onClick={() => navigate("/booking")}>
                {t("landing.hero.ctaDemo", "Book a Call")}
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline"
                className="w-full sm:w-auto text-lg px-8 py-4 bg-black text-white border-black hover:bg-white hover:text-black rounded-xl"
                onClick={() => navigate("/#pricing")}>
                {t("landing.hero.ctaStart", "Start free")}
              </Button>
            </div>
          </div>

          {/* Right — Dashboard mock — CSS animate on mount with slight delay */}
          <div className="relative scale-[0.85] lg:scale-100 origin-top animate-fade-in" style={{ animationDelay: "0.15s", animationFillMode: "both" }}>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-1 shadow-lg">
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-gray-200">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
                <div className="ml-4 flex-1 h-5 rounded-md bg-gray-100 max-w-[200px]" />
              </div>
              {/* Dashboard content */}
              <div className="p-6 space-y-4">
                {/* Stat cards row */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: t("landing.hero.mock.activePests", "Active Pests"), value: "12", icon: Bug, color: "text-red-400" },
                    { label: t("landing.hero.mock.treatments", "Treatments"), value: "48", icon: FlaskConical, color: "text-[hsl(195,64%,52%)]" },
                    { label: t("landing.hero.mock.costHa", "Cost/ha"), value: "€340", icon: TrendingDown, color: "text-[hsl(213,70%,51%)]" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-xl bg-white border border-gray-200 p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                        <span className="text-[10px] text-gray-500">{s.label}</span>
                      </div>
                      <span className="text-xl font-bold text-gray-900">{s.value}</span>
                    </div>
                  ))}
                </div>
                {/* Bar chart mock */}
                <div className="rounded-xl bg-white border border-gray-200 p-4">
                  <div className="text-[10px] text-gray-500 mb-3">{t("landing.hero.mock.weeklyActivity", "Weekly Treatment Activity")}</div>
                  <div className="flex items-end gap-2 h-20">
                    {[40, 65, 50, 80, 55, 70, 45].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t-sm" style={{
                        height: `${h}%`,
                        background: i === 3
                          ? "linear-gradient(180deg, hsl(177 58% 52%), hsl(213 70% 51%))"
                          : "linear-gradient(180deg, hsl(177 58% 52% / 0.3), hsl(213 70% 51% / 0.3))",
                      }} />
                    ))}
                  </div>
                  <div className="flex justify-between mt-2">
                    {[
                      t("landing.hero.mock.mon", "M"),
                      t("landing.hero.mock.tue", "T"),
                      t("landing.hero.mock.wed", "W"),
                      t("landing.hero.mock.thu", "T"),
                      t("landing.hero.mock.fri", "F"),
                      t("landing.hero.mock.sat", "S"),
                      t("landing.hero.mock.sun", "S"),
                    ].map((d, i) => (
                      <span key={i} className="text-[9px] text-gray-400 flex-1 text-center">{d}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating cards */}
            <FloatingCard className="top-16 -left-8 animate-float scale-[0.7] sm:scale-100 origin-top-left">
              <div className="flex items-center gap-2">
                <ProgressRing percent={78} />
                <div>
                  <div className="text-[11px] text-muted-foreground">{t("landing.hero.mock.treatmentPlan", "Treatment Plan")}</div>
                  <div className="text-sm font-bold text-foreground">{t("landing.hero.mock.percentComplete", "78% complete")}</div>
                </div>
              </div>
            </FloatingCard>

            <FloatingCard className="bottom-24 -right-6 animate-float-delayed scale-[0.7] sm:scale-100 origin-top-right">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-foreground">{t("landing.hero.mock.thripsAlert", "Thrips Alert")}</div>
                  <div className="text-[10px] text-muted-foreground">{t("landing.hero.mock.blockA3High", "Block A3 · High")}</div>
                </div>
              </div>
            </FloatingCard>

            <FloatingCard className="bottom-4 -left-4 animate-float-slow scale-[0.7] sm:scale-100 origin-bottom-left">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <TrendingDown className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-foreground">{t("landing.hero.mock.costReduction", "Cost Reduction")}</div>
                  <div className="text-sm font-bold text-green-800">-30%</div>
                </div>
              </div>
            </FloatingCard>
          </div>
        </div>
      </div>
    </section>
  );
}
